import React, { useMemo, useState } from "react";
import { esPool, piezasReservablesDe, unidadDe } from "../../utils/catalogo";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

// Qué tan usados están los activos de un servicio, día por día, en los cuatro
// meses que vienen. Responde la pregunta que ninguna lista contesta: "¿puedo
// comprometer 100 sillas el 14 de marzo?" — y la contesta de un vistazo, no
// leyendo evento por evento. Cuatro meses porque una temporada no cabe en uno y
// el ancho estaba ahí sin usarse.
//
// Todo sale de `recursosAsignados` de los eventos que VenueProvider ya tiene
// suscritos (los arma ServicioReservas y los pasa aquí): cero queries extra.
// Ver utils/unidades.js para el porqué de esa denormalización.
//
// La CAPACIDAD depende del modo del servicio:
//   por cantidad     → las piezas reservables (total − fuera de circulación)
//   identificadas    → cuántas piezas activas hay dadas de alta
//
// CÓMO SE LEE. Verde es DISPONIBILIDAD, no ocupación: un día sin nada apartado
// es verde entero, y el verde se VACÍA de arriba hacia abajo conforme se
// compromete. Sin verde = no queda nada. Al revés se leía como "verde = lleno",
// que es lo contrario de lo que el color dice en el resto del panel.
//
// El valor vive en la ALTURA del verde, no en el tono, así que un solo tono
// basta y quien no distingue verdes lee el mes igual de bien. Hubo antes un mapa
// de calor de cinco tintes con una barrita de 3 px en la base: dos marcas por
// celda peleando en 24 px, y el color cargando una cifra que no puede cargar
// —nadie separa 40 % de 60 % sin ir a la leyenda—.
//
// Lo comprometido queda en ámbar y no en gris: el día más apretado del mes es
// justo el que tiene que saltar, y en gris se leía como día vacío. El rojo se
// reserva para lo que está mal: más comprometido que existente.
//
// Los números exactos están en la línea de detalle, que reacciona al cursor Y al
// foco del teclado: el `title` nativo tarda un segundo, no se estiliza y en una
// pantalla táctil no existe (se deja igual, de refuerzo).
//
// El verde es el de marca, el mismo de la rampa de "agenda vendida" de los
// reportes (components/reportes/shared.js), para que el panel se lea como un
// sistema.
// Tonos CLAROS los dos. Como el dato vive en la altura, el color no tiene que
// pesar: con el verde saturado, un trimestre libre —que es el estado normal— era
// un muro oscuro de 120 celdas, y además obligaba a voltear la tinta del número
// a blanco a media celda. En claro, el número va en un solo gris en todos los
// días y el ojo lee la silueta del llenado, que es el punto.
const LIBRE = "#C2E3CF";
const COMPROMETIDO = "#FAEBCF";
const DIA_TEXTO = "#3F3F46";
// El único que grita, porque es un error y es raro: más apartado que existente.
const SOBREVENDIDO = "#DC2626";
const SOBREVENDIDO_TEXTO = "#FFFFFF";

const MESES_VISIBLES = 4;
const DIAS = ["L", "M", "M", "J", "V", "S", "D"];
const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const MES_CORTO = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];
const SEMANA = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

// Lunes = 0, para que la semana empiece donde la empieza el calendario del app.
const columnaDe = (date) => (date.getDay() + 6) % 7;

const mismoDia = (a, b) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

export default function ServicioCalendarioUso({ item, reservas = [], unidades = [] }) {
  const hoy = new Date();
  const [desde, setDesde] = useState(() => new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  // El día que el cursor o el teclado tiene encima; null = se resume el periodo.
  const [activo, setActivo] = useState(null);

  const pool = esPool(item);
  // En identificadas, lo que se puede comprometer son las piezas activas: una
  // en mantenimiento o de baja no sale a ningún evento.
  const capacidad = pool
    ? piezasReservablesDe(item)
    : (unidades || []).filter((u) => (u.estado || "activo") === "activo").length;
  const unidad = unidadDe(item);

  const meses = useMemo(() => {
    const salida = [];
    for (let m = 0; m < MESES_VISIBLES; m += 1) {
      const anio = desde.getFullYear();
      const mesIdx = desde.getMonth() + m;
      const primero = new Date(anio, mesIdx, 1);
      const cuantos = new Date(anio, mesIdx + 1, 0).getDate();
      const dias = Array.from({ length: cuantos }, (_, i) => {
        const dia = new Date(primero.getFullYear(), primero.getMonth(), i + 1);
        const inicioDia = new Date(dia.getFullYear(), dia.getMonth(), i + 1, 0, 0, 0, 0);
        const finDia = new Date(dia.getFullYear(), dia.getMonth(), i + 1, 23, 59, 59, 999);
        const delDia = reservas.filter((r) => r.inicio <= finDia && r.fin >= inicioDia);
        // Por cantidad se suman; identificadas se cuentan las piezas distintas
        // (dos eventos del mismo día no pueden compartir pieza, pero un evento
        // puede llevar varias).
        const ocupadas = pool
          ? delDia.reduce((s, r) => s + (Number(r.asignacion?.cantidad) || 0), 0)
          : new Set(
              delDia.flatMap((r) =>
                Array.isArray(r.asignacion?.unidadIds) ? r.asignacion.unidadIds : []
              )
            ).size;
        return {
          clave: `${dia.getFullYear()}-${dia.getMonth()}-${i + 1}`,
          dia,
          numero: i + 1,
          ocupadas,
          disponibles: Math.max(0, capacidad - ocupadas),
          sobrevendido: ocupadas > capacidad,
          pct: capacidad > 0 ? Math.min(1, ocupadas / capacidad) : 0,
          eventos: delDia.map((r) => r.evento?.nombre || "Sin nombre"),
        };
      });
      salida.push({
        clave: `${primero.getFullYear()}-${primero.getMonth()}`,
        primero,
        titulo: `${MESES[primero.getMonth()]}${
          primero.getFullYear() !== hoy.getFullYear() ? ` ${primero.getFullYear()}` : ""
        }`,
        hueco: columnaDe(primero),
        dias,
      });
    }
    return salida;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, reservas, capacidad, pool]);

  const todos = useMemo(() => meses.flatMap((m) => m.dias), [meses]);
  const pico = todos.reduce((max, d) => Math.max(max, d.ocupadas), 0);
  const conUso = todos.filter((d) => d.ocupadas > 0).length;
  const ultimo = meses[meses.length - 1].primero;
  const rango =
    `${MES_CORTO[desde.getMonth()]} ${desde.getFullYear()} – ` +
    `${MES_CORTO[ultimo.getMonth()]} ${ultimo.getFullYear()}`;

  const mover = (delta) =>
    setDesde((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  const enElPeriodo = meses.some((m) => mismoDia(m.primero, new Date(hoy.getFullYear(), hoy.getMonth(), 1)));

  const detalleDe = (d) => {
    const fecha = `${SEMANA[columnaDe(d.dia)]} ${d.numero} de ${MESES[d.dia.getMonth()]}`;
    if (d.sobrevendido) {
      return {
        fecha,
        cifra: `${d.ocupadas} apartadas y solo tienes ${capacidad}: te faltan ${
          d.ocupadas - capacidad
        }`,
        tono: "text-red-600",
        eventos: d.eventos,
      };
    }
    return {
      fecha,
      // Primero lo que queda, que es la pregunta que se viene a hacer.
      cifra:
        d.ocupadas === 0
          ? `las ${capacidad} disponibles`
          : `${d.disponibles} de ${capacidad} disponibles · ${d.ocupadas} apartadas`,
      tono: d.disponibles === 0 ? "text-amber-700" : "text-zinc-700 dark:text-zinc-300",
      eventos: d.eventos,
    };
  };

  if (capacidad === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm text-sm text-zinc-500">
        {pool
          ? "Pon cuántas piezas tienes en total (en Editar) para ver qué tan usadas están."
          : "Da de alta las piezas para ver qué tan usadas están."}
      </div>
    );
  }

  const detalle = activo ? detalleDe(activo) : null;

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Disponibilidad de tus {capacidad} {unidad}
          {capacidad === 1 ? "" : "s"}
        </span>
        <span className="text-xs text-zinc-400">{rango}</span>
        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => mover(-1)}
            className="p-0.5 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700/60"
            title="Un mes atrás"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          {!enElPeriodo && (
            <button
              type="button"
              onClick={() => setDesde(new Date(hoy.getFullYear(), hoy.getMonth(), 1))}
              className="px-1.5 text-xs font-semibold hover:underline"
              style={{ color: "#1b4d2e" }}
            >
              Hoy
            </button>
          )}
          <button
            type="button"
            onClick={() => mover(1)}
            className="p-0.5 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-700/60"
            title="Un mes adelante"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Cuatro meses de corrido. Con el ancho de una laptop caben en fila; al
          angostarse pasan a dos y luego a uno, sin que las celdas se estiren. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-5 gap-y-4">
        {meses.map((m) => (
          <div key={m.clave}>
            <div className="text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1.5 capitalize">
              {m.titulo}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {DIAS.map((d, i) => (
                <div
                  key={i}
                  className="text-center text-[9px] font-semibold uppercase tracking-wide text-zinc-400"
                >
                  {d}
                </div>
              ))}
              {Array.from({ length: m.hueco }, (_, i) => (
                <div key={`hueco-${i}`} />
              ))}
              {m.dias.map((d) => {
                // La altura del verde ES lo disponible.
                const libre = d.sobrevendido ? 0 : 1 - d.pct;
                const esHoy = mismoDia(d.dia, hoy);
                const det = detalleDe(d);
                const titulo = [det.fecha, det.cifra, det.eventos.join(", ") || null]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <button
                    key={d.clave}
                    type="button"
                    title={titulo}
                    aria-label={titulo}
                    onMouseEnter={() => setActivo(d)}
                    onMouseLeave={() => setActivo(null)}
                    onFocus={() => setActivo(d)}
                    onBlur={() => setActivo(null)}
                    className={`relative aspect-square rounded-sm overflow-hidden text-[10px] leading-none font-medium transition ${
                      esHoy ? "ring-1 ring-inset ring-zinc-500" : ""
                    } ${activo?.clave === d.clave ? "ring-2 ring-inset ring-zinc-900/40" : ""}`}
                    style={{
                      backgroundColor: d.sobrevendido ? SOBREVENDIDO : COMPROMETIDO,
                      color: d.sobrevendido ? SOBREVENDIDO_TEXTO : DIA_TEXTO,
                    }}
                  >
                    {/* El verde sube desde abajo: su altura es lo que queda. */}
                    {libre > 0 && (
                      <span
                        className="absolute inset-x-0 bottom-0"
                        style={{
                          height: `${Math.round(libre * 100)}%`,
                          backgroundColor: LIBRE,
                        }}
                      />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center">
                      {d.numero}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Línea de detalle: siempre está, así que nada se mueve al pasar el
          cursor, y sin cursor resume el periodo. */}
      <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-700/40 min-h-[2.5rem]">
        {detalle ? (
          <>
            <div className="text-sm">
              <span className="font-medium text-zinc-900 dark:text-white capitalize">
                {detalle.fecha}
              </span>
              <span className={`ml-2 ${detalle.tono}`}>{detalle.cifra}</span>
            </div>
            {detalle.eventos.length > 0 && (
              <div className="mt-0.5 text-xs text-zinc-500 truncate">
                {detalle.eventos.join(" · ")}
              </div>
            )}
          </>
        ) : (
          <div className="text-xs text-zinc-500">
            {conUso === 0 ? (
              <>
                Todo libre en estos cuatro meses: ninguna pieza está apartada. Pasa el
                cursor por un día para ver el detalle.
              </>
            ) : (
              <>
                {conUso} {conUso === 1 ? "día" : "días"} con piezas apartadas · el día más
                apretado deja {capacidad - pico} de {capacidad} libres. Pasa el cursor por
                un día para ver el detalle.
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex gap-px">
            {[1, 0.75, 0.5, 0.25, 0].map((libre) => (
              <span
                key={libre}
                className="relative h-3.5 w-3.5 rounded-sm overflow-hidden"
                style={{ backgroundColor: COMPROMETIDO }}
              >
                {libre > 0 && (
                  <span
                    className="absolute inset-x-0 bottom-0"
                    style={{ height: `${libre * 100}%`, backgroundColor: LIBRE }}
                  />
                )}
              </span>
            ))}
          </span>
          todo libre → nada libre
        </span>
        <span>El verde de cada día es lo que queda sin comprometer.</span>
      </div>
    </div>
  );
}
